// SettingsPlanPane.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import useSettingsPlanPaneState from '../hooks/useSettingsPlanPaneState';
import useSettingsPlanPaneViewModel from '../hooks/useSettingsPlanPaneViewModel';

// Components
import SettingsPlanBillingCard from './SettingsPlanBillingCard';
import SettingsPlanCatalogGrid from './SettingsPlanCatalogGrid';
import SettingsPlanCheckoutCard from './SettingsPlanCheckoutCard';
import SettingsPlanOverviewCards from './SettingsPlanOverviewCards';

/**
 * SettingsPlanPane
 *
 * Renders the workspace plan catalog and allows self-serve selection for
 * public free plans.
 *
 * @param {object} user - Active user model.
 * @param {object} workspace - Active workspace model.
 * @param {boolean} canEditWorkspace - Whether the current user can edit workspace settings.
 *
 * @returns {ReactNode} The rendered plan settings pane.
 */
const SettingsPlanPane = React.memo(({
    user,
    workspace,
    canEditWorkspace,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    const {
        planState,
        billingState,
        usageState,
        messageState,
        handlers,
    } = useSettingsPlanPaneState({
        user,
        workspace,
        canEditWorkspace,
    });

    const {
        plans,
        isLoading,
        savingPlanKey,
        currentPlanKey,
        currentPlan,
        currentPlanTitle,
        isCurrentPlanHiddenFromCatalog,
        checkoutPlanKey,
        checkoutPlan,
    } = planState;
    const {
        billingRecord,
        billingLoading,
        billingSubscriptionId,
        hasActivePaidSubscription,
        isCancelSubscriptionConfirming,
        isCancellingSubscription,
        canCancelPaidSubscription,
        isOpeningBillingPortal,
        canOpenBillingPortal,
    } = billingState;
    const {
        apiLimits,
        apiUsageLoading,
        apiUsageError,
        liveUsage,
        liveUsageLoading,
        liveUsageError,
    } = usageState;
    const {
        errorMessage,
        statusMessage,
    } = messageState;
    const {
        handleSelectPlan,
        handleStartPaidCheckout,
        handleCancelPaidCheckout,
        handleStartCancelSubscription,
        handleAbortCancelSubscription,
        handleCancelSubscription,
        handleOpenBillingPortal,
        handlePaidCheckoutSuccess,
    } = handlers;

    const {
        apiUsageSummary,
        billingRenewsText,
        billingStatusText,
        currentPlanPriceText,
        liveUsageSummary,
        membersUsageText,
        shouldShowApiUsage,
        shouldShowLiveUsage,
        shouldShowRecordingUsage,
        storageReservedNote,
        storageUsageText,
    } = useSettingsPlanPaneViewModel({
        apiLimits,
        billingLoading,
        billingRecord,
        currentPlan,
        isCurrentPlanHiddenFromCatalog,
        liveUsage,
        workspace,
    });

    // ----- RENDER ----- //

    return (
        <>
            <div className="settings-modal-pane-header">
                <div className="settings-modal-pane-header-left">
                    <div
                        className="settings-modal-pane-title"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        Plan
                    </div>
                    <div
                        className="settings-modal-pane-subtitle"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Choose the public plan for this workspace, start paid checkout, and cancel active billing here.
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">
                <div className="settings-modal-plan-stack">
                    <SettingsPlanOverviewCards
                        apiUsageError={apiUsageError}
                        apiUsageLoading={apiUsageLoading}
                        apiUsageSummary={apiUsageSummary}
                        billingRenewsText={billingRenewsText}
                        billingStatusText={billingStatusText}
                        currentPlanPriceText={currentPlanPriceText}
                        currentPlanTitle={currentPlanTitle}
                        isCurrentPlanHiddenFromCatalog={isCurrentPlanHiddenFromCatalog}
                        liveUsageError={liveUsageError}
                        liveUsageLoading={liveUsageLoading}
                        liveUsageSummary={liveUsageSummary}
                        membersUsageText={membersUsageText}
                        shouldShowApiUsage={shouldShowApiUsage}
                        shouldShowLiveUsage={shouldShowLiveUsage}
                        shouldShowRecordingUsage={shouldShowRecordingUsage}
                        storageReservedNote={storageReservedNote}
                        storageUsageText={storageUsageText}
                        theme={theme}
                    />

                    {!canEditWorkspace && (
                        <div
                            className="settings-modal-note"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Only admins and owners can change the workspace plan.
                        </div>
                    )}

                    <SettingsPlanBillingCard
                        billingRecord={billingRecord}
                        billingSubscriptionId={billingSubscriptionId}
                        canCancelPaidSubscription={canCancelPaidSubscription}
                        canOpenBillingPortal={canOpenBillingPortal}
                        currentPlanTitle={currentPlanTitle}
                        isCancelSubscriptionConfirming={isCancelSubscriptionConfirming}
                        isCancellingSubscription={isCancellingSubscription}
                        isOpeningBillingPortal={isOpeningBillingPortal}
                        onAbortCancelSubscription={handleAbortCancelSubscription}
                        onCancelSubscription={handleCancelSubscription}
                        onOpenBillingPortal={handleOpenBillingPortal}
                        onStartCancelSubscription={handleStartCancelSubscription}
                        theme={theme}
                    />

                    <div
                        className="settings-modal-note"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Free plans can be selected immediately. Paid plans start Stripe checkout here, and active billing must be canceled before switching back to free.
                    </div>

                    {checkoutPlan ? (
                        <SettingsPlanCheckoutCard
                            billingRecord={billingRecord}
                            plan={checkoutPlan}
                            theme={theme}
                            user={user}
                            workspace={workspace}
                            onCancel={handleCancelPaidCheckout}
                            onSuccess={handlePaidCheckoutSuccess}
                        />
                    ) : null}

                    {isLoading && (
                        <div
                            className="settings-modal-note"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Loading public plans...
                        </div>
                    )}

                    {!isLoading && !plans.length && !errorMessage && (
                        <div
                            className="settings-modal-note"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            No public plans are available right now.
                        </div>
                    )}

                    {!isLoading && plans.length > 0 && (
                        <SettingsPlanCatalogGrid
                            canEditWorkspace={canEditWorkspace}
                            checkoutPlanKey={checkoutPlanKey}
                            currentPlanKey={currentPlanKey}
                            hasActivePaidSubscription={hasActivePaidSubscription}
                            onSelectPlan={handleSelectPlan}
                            onStartPaidCheckout={handleStartPaidCheckout}
                            plans={plans}
                            savingPlanKey={savingPlanKey}
                            theme={theme}
                        />
                    )}

                    {statusMessage && !errorMessage && (
                        <div className="settings-modal-status">{statusMessage}</div>
                    )}

                    {errorMessage && (
                        <div className="settings-modal-error">{errorMessage}</div>
                    )}
                </div>
            </div>
        </>
    );
});

// Dev Tools
SettingsPlanPane.displayName = 'SettingsPlanPane';

export default SettingsPlanPane;